import { Box, Button, TextField, Typography } from '@mui/material';
import { useEffect } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';

import VNSelect from '@/components/VNSelect/VNSelect';
import VNTextField from '@/components/VNTextField/VNTextField';
import { RecipientType } from '@/models/ExternalNotification';

type Option = { label: string; value: string };

type IProps = {
  name: string;
  label: string;
  required?: boolean;
  type?: string; // e.g., "RecipientConfig[]"
  options?: Option[];
  allowEditRecipientConfig?: boolean;
  predefinedRecipientConfig?: { recipientType: string; value: string }[];
  supportsTemplateExpressions?: boolean;
  placeholders?: [];
};

const Recipients = ({
  name,
  label,
  required = false,
  type = 'string',
  options = [],
  allowEditRecipientConfig = false,
  predefinedRecipientConfig = [],
  supportsTemplateExpressions = false,
  placeholders = [],
}: IProps) => {
  const {
    control,
    register,
    setValue,
    formState: { errors, isSubmitted },
  } = useFormContext();

  const { fields, append, remove, replace } = useFieldArray({ control, name });

  const arrayErrors = errors?.[name as keyof typeof errors];
  const showArrayError = required && isSubmitted && (!fields || fields.length === 0);

  // Initialize predefined recipients if editing is disabled
  useEffect(() => {
    if (!allowEditRecipientConfig) {
      if (predefinedRecipientConfig?.length > 0) {
        // Load predefined recipients
        replace(predefinedRecipientConfig);
      } else {
        // No predefined recipients => clear the array
        replace([]);
      }
    }
  }, [allowEditRecipientConfig, predefinedRecipientConfig, replace]);

  const handleAdd = () => {
    if (type.startsWith('RecipientConfig')) {
      append({ recipientType: RecipientType.Email, value: '' });
    } else {
      append({ value: '' });
    }
  };

  return (
    <Box
      sx={{
        border: '1px solid #ccc',
        borderRadius: 2,
        p: 2,
        mt: 1,
        opacity: allowEditRecipientConfig ? 1 : 0.7,
      }}
    >
      <Typography sx={{ fontWeight: 600, mb: 1 }}>
        {label} {required && <span style={{ color: 'red' }}>*</span>}
      </Typography>

      {/* Add button when no fields exist and editing is allowed */}
      {fields.length === 0 && allowEditRecipientConfig && (
        <Button variant="contained" color="primary" size="small" onClick={handleAdd}>
          Add Item
        </Button>
      )}

      {fields.map((item, index) => {
        const baseName = `${name}.${index}` as const;

        const valueError = (Array.isArray(arrayErrors) && arrayErrors[index]?.value?.message) || '';
        const typeError = (Array.isArray(arrayErrors) && arrayErrors[index]?.recipientType?.message) || '';

        return (
          <Box
            key={item.id}
            sx={{
              display: 'flex',
              gap: 2,
              alignItems: 'center',
              mb: 1,
            }}
          >
            {/* For RecipientConfig[] */}
            {type.startsWith('RecipientConfig') ? (
              <>
                <VNSelect
                  label="Recipient Type"
                  name={`${baseName}.recipientType`}
                  options={options}
                  disabled={!allowEditRecipientConfig}
                />

                <Box width="100%">
                  <VNTextField
                    label="Value"
                    name={`${baseName}.value`}
                    disabled={!allowEditRecipientConfig}
                    placeholders={supportsTemplateExpressions ? placeholders : []}
                  />
                </Box>
              </>
            ) : (
              // Default type: string[]
              <TextField
                label={`${label} ${index + 1}`}
                {...register(`${baseName}.value`, {
                  required: required ? `${label} is required` : false,
                })}
                error={!!valueError}
                helperText={valueError as string}
                defaultValue={item.value || ''}
                fullWidth
                size="small"
                disabled={!allowEditRecipientConfig}
              />
            )}

            {/* Remove button only if editing is allowed */}
            {allowEditRecipientConfig && (
              <Button variant="contained" color="error" size="small" onClick={() => remove(index)}>
                Remove
              </Button>
            )}
          </Box>
        );
      })}

      {/* Add Another button only if editing is allowed */}
      {allowEditRecipientConfig && fields.length > 0 && (
        <Button variant="contained" color="primary" size="small" onClick={handleAdd}>
          Add Another
        </Button>
      )}

      {/* Required array-level validation */}
      {showArrayError && (
        <Typography color="error" sx={{ mt: 1, fontSize: 13 }}>
          At least one {label.toLowerCase()} is required
        </Typography>
      )}
    </Box>
  );
};

export default Recipients;
